/* eslint-disable max-statements */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import loadable from "@loadable/component";
import EChallanLoader from "../e-challan-loader";
import LoginSignup from "../login-signup-v2";
import { sendCustomGaEvents } from "../../../tracking";
import { mobileNumberEntered, otpVerification } from "./tracking";
import { useParams } from "react-router";
import { ACTION_TYPES } from "./selector";
import { HTTP_CODES } from "../../../constants/app-constants";
import { PROCESSING_STATUS } from "../../../constants/challan-constants";
import { firebaseRtdManager } from "../../../utils/firebase-rtd/manager";
import { PROSPECT_LEAD_DEVICE_TYPES } from "../../../constants/c2b-vas-constants";

const EChallanListContainer = loadable(() => import("../e-challan-list-container"));
const EchallanCart = loadable(() => import("../e-challan-cart"));

const sourceChannel = "challan";

const MyChallans = ({
    secureToken,
    mobile,
    regNo = "",
    history,
    userId,
    isLoggedIn,
    isUserDetailFetched,
    logoutUserConnect,
    markUnauthorizedConnect,
    createChallanProspectLeadConnect,
    getChallanListConnect,
    getChallanProspectLeadDetailsConnect,
    checkLeadPhoneNumberConnect,
    sendOtpViaLeadTokenConnect,
    verifyOtpViaLeadTokenConnect,
    verifyOtpConnect,
    maskedPhone = "",
    setMaskedPhoneConnect = () => {},
    emptyCartConnect,
    addToCartConnect,
    getCartDetailsConnect,
    removeFromCartConnect,
    getOrderListConnect,
    name,
    source,
    setGaSourceConnect
}) => {
    const { token = "", action = "", orderId = "" } = useParams() || {};

    const extraParams = {
        token,
        sourceChannel
    };

    const [isUserLoginRequired, setIsUserLoginRequired] = useState(true);
    const [isFetchingData, setIsFetchingData] = useState(true);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [orderList, setOrderList] = useState([]);

    useEffect(() => {
        if (source) {
            sendCustomGaEvents({us_vas_page_source: source});
            setGaSourceConnect(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);

    const onBack = () => {
        setIsUserLoginRequired(false);
        setIsFetchingData(false);
    };

    const handleFirebaseRtdConnection = ({listenableRtdPath, leadToken, rtdAuthToken}) => {
        const instance = firebaseRtdManager.getFirebaseDbInstance("C2B_VAS_RTD");
        firebaseRtdManager.signInAndProceedToNextStep({dbInstance: instance, key: listenableRtdPath, rtdAuthToken}, (response) => {
            if (response && response.processingStatus === PROCESSING_STATUS.PROCESSED) {
                firebaseRtdManager.disconnectFirebaseRtdConnection(instance, listenableRtdPath);
                getChallanListConnect({token: leadToken, authToken: secureToken }).then(() => {
                    history.replace(`/my-challans/${leadToken}`);
                    onBack();
                    setIsFetchingData(false);
                });
            }
        });
    };

    const fetchChallanDetails = (details) => {
        const {leadToken = token, authToken = secureToken} = details || {};
        getChallanListConnect({token: leadToken, authToken}).then(() => {
            history.replace(`/my-challans/${leadToken}`);
            onBack();
            setIsFetchingData(false);
        }).catch(() => {
            onBack();
            setIsFetchingData(false);
        });
    };

    const fetchCartDetails = (data) => {
        const {leadToken = token, isRemoval = false, actionType = ACTION_TYPES.CHECKOUT} = data || {};
        const actionStatus = actionType === ACTION_TYPES.SUMMARY && orderId ? actionType : ACTION_TYPES.CHECKOUT;
        getCartDetailsConnect({authToken: secureToken}).then((response) => {
            if (!((response || {}).cartItems || []).length && action === ACTION_TYPES.CHECKOUT) {
                fetchChallanDetails({leadToken, authToken: secureToken});
                setIsAddingToCart(false);
            } else {
                if (!isRemoval) history.replace(`/my-challans/${leadToken}/${actionStatus}/${orderId || ""}`);
                setIsAddingToCart(false);
                onBack();
                setIsFetchingData(false);
            }
        }).catch((e) => {
            if (e && e.code && e.code === `${HTTP_CODES.NOT_FOUND}`) {
                setIsAddingToCart(false);
                fetchChallanDetails();
            }
        });
    };

    const handleRemovalFromCart = (productUniqueIdentifier, challanNo) => {
        setIsAddingToCart(true);
        removeFromCartConnect({productUniqueIdentifier, authToken: secureToken}).then(() => {
            sendCustomGaEvents({
                event: "challan_removed",
                eventAction: "challan_removed",
                eventCategory: "Challan_pay",
                eventLabel: challanNo
            });
            fetchCartDetails({isRemoval: true});
        });
    };

    const addToCartAndFetchCartDetails = (payload) => {
        addToCartConnect({payload, authToken: secureToken}).then(() => {
            fetchCartDetails();
        });
    };

    const handleAddToCart = (payload, isEmptyCartRequired = false) => {
        setIsAddingToCart(true);
        if (isEmptyCartRequired) {
            emptyCartConnect({authToken: secureToken}).then(() => {
                addToCartAndFetchCartDetails(payload);
            });
        } else {
            addToCartAndFetchCartDetails(payload);
        }
    };

    const fetchChallanListForRegNo = async ({leadToken = token, authToken = secureToken, vehicleRegNo = regNo, resetSelectedChallans}, isBackFromUpdate = false) => {
        setIsFetchingData(true);
        setOrderList([]);
        if ((action === ACTION_TYPES.CHECKOUT || action === ACTION_TYPES.SUMMARY) && !isBackFromUpdate) {
            fetchCartDetails({leadToken, actionType: action});
        } else {
            getOrderListConnect({authToken}, "challan").then((response) => {
                const filteredList = response.list.filter(item => {
                    return item.orderItems.some(orderItem => {
                        return orderItem.product.registrationNumber === vehicleRegNo;
                    });
                });
                setOrderList(filteredList);
            });
            getChallanListConnect({token: leadToken, authToken, resetSelectedChallans}).then(({listenableRtdPath, processingStatus, rtdAuthToken}) => {
                if (listenableRtdPath && rtdAuthToken && processingStatus === PROCESSING_STATUS.PROCESSING) {
                    handleFirebaseRtdConnection({listenableRtdPath, leadToken, rtdAuthToken});
                } else {
                    history.replace(`/my-challans/${leadToken}`);
                    onBack();
                }
            }).catch(() => {
                onBack();
            });
        }
    };

    const handleSessionLoggedOut = () => {
        sendOtpViaLeadTokenConnect(token, sourceChannel).then(() => {
            setIsUserLoginRequired(true);
            setIsFetchingData(false);
        });
    };

    const logoutUser = () => {
        logoutUserConnect().then(() => {
            markUnauthorizedConnect();
        });
    };

    const checkLoginStatus = ({vehicleRegNo = regNo}) => {
        const payload = {
            token,
            phone: mobile,
            vasType: "prospect/lead"
        };
        checkLeadPhoneNumberConnect(payload).then((response) => {
            if (response.isPhoneChanged) {
                logoutUser();
                handleSessionLoggedOut();
            } else {
                fetchChallanListForRegNo({leadToken: token, vehicleRegNo});
                setIsUserLoginRequired(false);
            }
        });
    };

    const createLead = (phone, loggedInUserId, authToken = secureToken) => {
        const payload = {
            phone,
            vehicle_reg_no: regNo,
            user_id: loggedInUserId,
            whatsapp_consent: true,
            type: "challan",
            device_category: PROSPECT_LEAD_DEVICE_TYPES.DESKTOP,
            ...(name && {customer_name: name})
        };
        createChallanProspectLeadConnect(payload, authToken).then((response) => {
            emptyCartConnect({authToken});
            fetchChallanListForRegNo({leadToken: response.token, authToken, resetSelectedChallans: true});
        }).catch(() => {
            setOrderList([]);
            onBack();
        });
    };

    const handleUserJourney = () => {
        if (token) {
            getChallanProspectLeadDetailsConnect(token, true).then((leadResponse) => {
                if (isLoggedIn) {
                    checkLoginStatus({
                        vehicleRegNo: leadResponse && leadResponse.vehicleRegNo
                    });
                } else {
                    setIsUserLoginRequired(true);
                    setIsFetchingData(false);
                }
            }).catch(() => {
                history.replace("/traffic-challan/");
            });
        } else if (!token && regNo) {
            setMaskedPhoneConnect("");
            if (isLoggedIn) {
                createLead(mobile, userId);
            } else {
                setIsUserLoginRequired(true);
                setIsFetchingData(false);
            }
        } else {
            setMaskedPhoneConnect("");
            history.replace("/traffic-challan/");
        }
    };

    useEffect(() => {
        if (isUserDetailFetched) {
            handleUserJourney();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUserDetailFetched]);

    const onLoginSuccess = (response) => {
        sendCustomGaEvents(otpVerification);
        if (token) {
            const { token: userToken } = response;
            fetchChallanListForRegNo({leadToken: token, authToken: userToken});
        } else {
            const { user_id: loggedInUserId, phone_number: phone, token: authToken }  = response;
            createLead(phone, loggedInUserId, authToken);
        }
    };

    const onClose = () => {
        history.replace("/traffic-challan/");
    };

    const sendOtp = () => {
        sendCustomGaEvents({
            ...mobileNumberEntered
            // eventLabel: custPhone
        });
        handleSessionLoggedOut();
    };

    const onBackFromCart = (isBackFromUpdate = false) => {
        if (isBackFromUpdate) {
            fetchChallanListForRegNo({leadToken: token, authToken: secureToken}, isBackFromUpdate);
        } else {
            setIsFetchingData(true);
            getChallanListConnect({token, authToken: secureToken}).then(() => {
                history.replace(`/my-challans/${token}`);
                onBack();
                setIsFetchingData(false);
            }).catch(() => {
                onBack();
                setIsFetchingData(false);
            });
        }
    };

    if (isFetchingData) return <EChallanLoader isVariant />;

    if (action === ACTION_TYPES.CHECKOUT) {
        return (
            <EchallanCart
                token={token}
                onBack={onBackFromCart}
                handleRemovalFromCart={handleRemovalFromCart}
            />
        );
    }

    return (
        <LoginSignup
            isVisible={isUserLoginRequired}
            onClose= {() => onClose()}
            source={"Seller"}
            useCustomOtpHandler={!!token}
            handleCustomOtpSend={sendOtp}
            handleCustomOtpVerification={token ? verifyOtpViaLeadTokenConnect : verifyOtpConnect}
            onLoginSuccessCallback={onLoginSuccess}
            extraParams={extraParams}
            customPhone={maskedPhone}
        >
            <EChallanListContainer
                isAddingToCart={isAddingToCart}
                handleAddToCart={handleAddToCart}
                token={token}
                orderList={orderList}
                setOrderList={setOrderList}
            />
        </LoginSignup>
    );
};

MyChallans.propTypes = {
    secureToken: PropTypes.string,
    mobile: PropTypes.string,
    regNo: PropTypes.string,
    history: PropTypes.object,
    isLoggedIn: PropTypes.bool,
    userId: PropTypes.string,
    createChallanProspectLeadConnect: PropTypes.func,
    getChallanListConnect: PropTypes.func,
    getChallanProspectLeadDetailsConnect: PropTypes.func,
    isUserDetailFetched: PropTypes.bool,
    checkLeadPhoneNumberConnect: PropTypes.func,
    sendOtpViaLeadTokenConnect: PropTypes.func,
    verifyOtpViaLeadTokenConnect: PropTypes.func,
    logoutUserConnect: PropTypes.func,
    markUnauthorizedConnect: PropTypes.func,
    verifyOtpConnect: PropTypes.func,
    maskedPhone: PropTypes.string,
    setMaskedPhoneConnect: PropTypes.func,
    emptyCartConnect: PropTypes.func,
    addToCartConnect: PropTypes.func,
    getCartDetailsConnect: PropTypes.func,
    removeFromCartConnect: PropTypes.func,
    getOrderListConnect: PropTypes.func,
    name: PropTypes.string,
    source: PropTypes.string,
    setGaSourceConnect: PropTypes.func
};

export default MyChallans;
